import { createBrowserRouter, Navigate, redirect } from "react-router-dom";
import { ProtectedRoutes } from "./protected-routes";
import React, { Suspense } from "react";
import FallbackLoad from "../components/fallback-load";
import SplashScreen from "../../main/signin-page/splash-screen";
import ClientPage from "../../main/client/client-page";
import EditProfile from "../../main/edit-profile/edt-profile-page";
import AnnouncementDetailPage from "../../main/home/components/announcement-details";
import OTPPage from "../../main/signin-page/components/otp";
import MfaTotpPage from "../../main/signin-page/components/mfa-totp";
import MfaSetupPage from "../../main/signin-page/components/mfa-setup";
import UserManagement from "../../main/user-management";
import { RefreshingTokenPage } from "../auth/refreshing-token-page";
import ValidateProductSession from "../../main/auth-pages/validate-product-session";
import ValidateDeskSession from "../../main/auth-pages/validate-desk-session";
import Notifications from "../../main/notifications";
import queryClient from "../query-client";
import { api } from "../base-api";
import Dashboard from "../../main/dashboard";
const Sessions = React.lazy(() => import("../../main/sessions"));

// Lazy-loaded components
const App = React.lazy(() => import("../../App"));
const MainWrapper = React.lazy(() => import("../components/main-wrapper"));
const SiginPage = React.lazy(() => import("../../main/signin-page/sigin-page"));
const HomePage = React.lazy(() => import("../../main/home/home-page"));
const BasicInformation = React.lazy(
  () =>
    import("../../main/client/sub-modules/basic-information/basic-information")
);
const ReviewDetails = React.lazy(
  () => import("../../main/client/sub-modules/review-details/review-details")
);
const ClientCredentials = React.lazy(
  () =>
    import(
      "../../main/client/sub-modules/client-credentials/client-credentials"
    )
);
const ChangeEmailPage = React.lazy(
  () => import("../../main/account-management/change-email")
);
const ChangePasswordPage = React.lazy(
  () => import("../../main/account-management/change-password")
);
const Unauthorized = React.lazy(() => import("./unauthorized"));
const ErrorPage = React.lazy(() => import("../../main/error-pages"));
const ForgotPasswordForm = React.lazy(
  () => import("../../main/signin-page/components/forgot-password-form")
);
const ResetPasswordLink = React.lazy(
  () => import("../../main/signin-page/components/reset-password-link")
);
const ConfirmEmail = React.lazy(
  () => import("../../main/account-management/confirm-email")
);
const OldFacilities = React.lazy(() => import("../../main/old-facilities"));
const Facilities = React.lazy(() => import("../../main/facilities"));
const AddFacility = React.lazy(
  () => import("../../main/old-facilities/components/add-facility")
);
const FacilityUsers = React.lazy(() => import("../../main/users-facility"));
const AddFacilityUser = React.lazy(
  () => import("../../main/users-facility/components/add-user")
);
const Products = React.lazy(() => import("../../main/products"));
const Clients = React.lazy(() => import("../../main/clients"));
const Users = React.lazy(() => import("../../main/users"));
const AccessibilityManagement = React.lazy(
  () => import("../../main/user-management/accessibility-management")
);

const BizboxAdmin = React.lazy(
  () =>
    import(
      "../../main/user-management/accessibility-management/children/bizbox-admin"
    )
);
const BizboxUser = React.lazy(
  () =>
    import(
      "../../main/user-management/accessibility-management/children/bizbox-user"
    )
);
const GroupAdmin = React.lazy(
  () =>
    import(
      "../../main/user-management/accessibility-management/children/group-admin"
    )
);
const CoGroupAdmin = React.lazy(
  () =>
    import(
      "../../main/user-management/accessibility-management/children/co-group-admin"
    )
);
const FacilityAdmin = React.lazy(
  () =>
    import(
      "../../main/user-management/accessibility-management/children/facility-admin"
    )
);
const FacilityUser = React.lazy(
  () =>
    import(
      "../../main/user-management/accessibility-management/children/facility-user"
    )
);

export const router = createBrowserRouter([
  // --- Public Routes ---
  {
    // A wrapper for all public routes, useful for common layouts or error boundaries
    // Note: No path here means it's a layout route that applies to its children
    // OR you can give it a specific path like '/' and then exclude protected routes from children
    // For simplicity, let's keep public routes as direct children of the router for now.
    // Or, you can group them under a parent like this:
    // path: "/", // This could be your overall application layout
    // element: <PublicLayout />, // A layout that doesn't enforce auth
    // children: [ ... public routes ... ]

    // Initial Splash Screen (root path for unauthenticated users)
    path: "/",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <SplashScreen />
      </Suspense>
    ),
  },
  {
    path: "/login",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <SiginPage />
      </Suspense>
    ),

    loader: async ({ request }) => {
      try {
        const baseURL = process.env.REACT_APP_BASE_URL;
        const url = new URL(request.url);
        const callbackUrl = url.searchParams.get("callback");
        const origin = url.searchParams.get("origin");
        const skipSessionCheck =
          typeof window !== "undefined" &&
          sessionStorage.getItem("skipSessionCheck") === "1";


        if (skipSessionCheck) {
          sessionStorage.removeItem("skipSessionCheck");
          return null;
        }
        // Use credentials: 'include' to ensure cookies are sent with the request
        const response = await fetch(
          `${baseURL}/api/auth/validate-user-session`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include", // IMPORTANT: This sends cookies
          }
        );

        const isValidated = response.ok;

        // If the user is authenticated, redirect them to the home page.
        if (isValidated) {
          if (callbackUrl) {
            // Redirect to the callback URL if it exists
            return redirect(`${callbackUrl}?origin=${origin}`);
          }

          return redirect("/home");
        }

        // If the user is not authenticated, the loader will return nothing.
        // This allows the route's component (e.g., the login page) to be rendered.
        return null;
      } catch (error) {
        console.error("Session validation failed:", error);
        // If there's a network error or the server is down, you might
        // want to return an error object or redirect to an error page.
        // For now, returning null will let the login page render.
        return null;
      }
    },
  },
  {
    path: "/auth/0",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <RefreshingTokenPage />
      </Suspense>
    ),
  },
  {
    path: "/otp",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <OTPPage />
      </Suspense>
    ),
  },
  {
    path: "/mfa-totp",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <MfaTotpPage />
      </Suspense>
    ),
  },
  {
    path: "/mfa-setup",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <MfaSetupPage />
      </Suspense>
    ),
  },
  {
    path: "/unauthorized/:statusCode",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <Unauthorized />
      </Suspense>
    ),
  },
  {
    path: "/error/:statusCode",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <ErrorPage />
      </Suspense>
    ),
  },
  // Short-form aliases (no slash): /error401, /error403, etc.
  { path: "/error401", element: <Navigate to="/error/401" replace /> },
  { path: "/error403", element: <Navigate to="/error/403" replace /> },
  { path: "/error404", element: <Navigate to="/error/404" replace /> },
  { path: "/error500", element: <Navigate to="/error/500" replace /> },
  { path: "/error503", element: <Navigate to="/error/503" replace /> },
  {
    path: "/forgot-password",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <ForgotPasswordForm />
      </Suspense>
    ),
  },
  {
    path: "/reset-password-link",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <ResetPasswordLink />
      </Suspense>
    ),
  },
  {
    path: "/confirm-email",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <ConfirmEmail />
      </Suspense>
    ),
  },
  {
    path: "/auth/validate-product-session",
    // THIS IS THE ROUTE THAT SHOULD NOT BE PROTECTED
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <ValidateProductSession />
      </Suspense>
    ),
  },
  {
    // Bizbox Desk SSO landing — product-agnostic mint (isolated from validate-product-session).
    // Public route (must NOT be protected), same as validate-product-session.
    path: "/auth/validate-desk-session",
    element: (
      <Suspense fallback={<FallbackLoad />}>
        <ValidateDeskSession />
      </Suspense>
    ),
  },

  // --- Protected Routes ---
  {
    // This is the main entry point for ALL protected routes.
    // All routes inside 'children' here will be protected by ProtectedRoutes.
    path: "/", // IMPORTANT: This path should still be '/', but it's now a *different* route object.
    // React Router will pick the first match. Since specific public routes
    // are listed before this, they will be matched first.
    element: (
      <ProtectedRoutes>
        <Suspense fallback={<FallbackLoad />}>
          <App />{" "}
          {/* App contains LandingPage which has Outlet, so protected content renders within App */}
        </Suspense>
      </ProtectedRoutes>
    ),
    errorElement: <>PAGE NOT FOUND</>, // For errors within this protected branch
    children: [
      {
        // This is where your actual authenticated content structure begins
        // All routes listed below here will implicitly require authentication
        element: (
          <Suspense fallback={<FallbackLoad />}>
            <MainWrapper />{" "}
            {/* MainWrapper likely contains an Outlet for its children */}
          </Suspense>
        ),
        children: [
          {
            path: "/home",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <HomePage />
              </Suspense>
            ),
          },
          {
            path: "/announcement/:id",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <AnnouncementDetailPage />
              </Suspense>
            ),
          },
          {
            path: "/dashboard",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <Dashboard />
              </Suspense>
            ),
          },
          {
            path: "/clients",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <Clients />
              </Suspense>
            ),
          },
          // {
          //   path: "/client",
          //   element: (
          //     <Suspense fallback={<FallbackLoad />}>
          //       <ClientPage />
          //     </Suspense>
          //   ),
          //   children: [
          //     {
          //       path: "basic-information",
          //       element: (
          //         <Suspense fallback={<FallbackLoad />}>
          //           <BasicInformation />
          //         </Suspense>
          //       ),
          //     },
          //     {
          //       path: "review",
          //       element: (
          //         <Suspense fallback={<FallbackLoad />}>
          //           <ReviewDetails />
          //         </Suspense>
          //       ),
          //     },
          //     {
          //       path: "credentials",
          //       element: (
          //         <Suspense fallback={<FallbackLoad />}>
          //           <ClientCredentials />
          //         </Suspense>
          //       ),
          //     },
          //   ],
          // },
          {
            path: "/account",
            children: [
              {
                path: "change-email",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <ChangeEmailPage />
                  </Suspense>
                ),
              },
              {
                path: "change-password",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <ChangePasswordPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/facilities",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <Facilities />
              </Suspense>
            ),
            children: [
              {
                path: "add-facility",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <AddFacility />
                  </Suspense>
                ),
              },
            ],
          },
          {
            // This structure with duplicate paths like "/facilities/:userId/:productCode/add-facility"
            // might need review to avoid ambiguity or allow for cleaner parameter passing.
            // Consider if this could be handled with query parameters or a single route.
            path: "/facilities/:userId/:productCode", // Specific route
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <OldFacilities />
              </Suspense>
            ),
            children: [
              {
                path: "add-facility",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <AddFacility />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/facility/users/:productCode/:facilityCode",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <FacilityUsers />
              </Suspense>
            ),
            children: [
              {
                path: "add-facility-user",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <AddFacilityUser />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: "/products",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <Products />
              </Suspense>
            ),
          },
          {
            path: "/edit-profile",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <EditProfile />
              </Suspense>
            ),
          },

          {
            path: "/sessions",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <Sessions />
              </Suspense>
            ),
          },
          {
            path: "/notifications",
            element: (
              <Suspense fallback={<FallbackLoad />}>
                <Notifications />
              </Suspense>
            ),
          },
          {
            path: "/user-management",
            children: [
              {
                path: "accessibility",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <AccessibilityManagement />
                  </Suspense>
                ),
                children: [
                  {
                    path: "bizbox-admin",
                    element: (
                      <Suspense fallback={<FallbackLoad />}>
                        <BizboxAdmin />
                      </Suspense>
                    ),
                  },
                  {
                    path: "bizbox-user",
                    element: (
                      <Suspense fallback={<FallbackLoad />}>
                        <BizboxUser />
                      </Suspense>
                    ),
                  },
                  {
                    path: "group-admin",
                    element: (
                      <Suspense fallback={<FallbackLoad />}>
                        <GroupAdmin />
                      </Suspense>
                    ),
                  },
                  {
                    path: "co-group-admin",
                    element: (
                      <Suspense fallback={<FallbackLoad />}>
                        <CoGroupAdmin />
                      </Suspense>
                    ),
                  },
                  {
                    path: "facility-admin",
                    element: (
                      <Suspense fallback={<FallbackLoad />}>
                        <FacilityAdmin />
                      </Suspense>
                    ),
                  },
                  {
                    path: "facility-user",
                    element: (
                      <Suspense fallback={<FallbackLoad />}>
                        <FacilityUser />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "facility-users",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <Users />
                  </Suspense>
                ),
              },
              {
                path: ":mode",
                element: (
                  <Suspense fallback={<FallbackLoad />}>
                    <UserManagement />
                  </Suspense>
                ),
              },
            ],
          },

          // Catch-all for protected routes (404 within protected area)
          { path: "*", element: <Navigate to="/error/404" replace /> },
        ],
      },
    ],
  },
  // Global catch-all for any unmatched route
  { path: "*", element: <Navigate to="/error/404" replace /> },
]);
